import { createStore } from "zustand/vanilla";
import type {
  GroupCreationMode,
  InitialGroupValues,
  PackageCreationAbilities,
  PackageCreationSettings,
  PackageCreationWizardStep,
  PackageDetailsValues,
  PopulationSubject,
  SectionConfiguration,
  SubjectConfiguration,
  ValidationPresentation,
} from "../types";
import { getLockedVacancies } from "../utils/group-vacancy-source";

export interface PackageWithLeaguesState {
  currentStep: PackageCreationWizardStep;
  packageDetails: PackageDetailsValues;
  isJointCreationSelected: boolean;
  abilities: PackageCreationAbilities;
  settings: PackageCreationSettings;
  subjects: SubjectConfiguration[];
  shouldCreateInitialGroups: boolean;
  leagueConfigurationSourceKey: string | null;
  initialGroupConfigurationSourceKey: string | null;
  validationMessagesByCourseId: Record<string, ValidationPresentation[]>;
  globalValidationMessages: ValidationPresentation[];
}

export interface PackageWithLeaguesActions {
  goForward: () => void;
  goBack: () => void;
  setPackageDetails: (values: Partial<PackageDetailsValues>) => void;
  prepareLeagueConfiguration: (sourceKey: string) => void;
  prepareInitialGroupConfiguration: (sourceKey: string) => void;
  setJointCreationSelected: (selected: boolean) => void;
  configureFlow: (abilities: PackageCreationAbilities, settings: PackageCreationSettings) => void;
  initializeSubjects: (subjects: PopulationSubject[]) => void;
  selectCreateNewLeague: (courseId: string) => void;
  selectExistingLeague: (
    courseId: string,
    existingLeagueId?: string | null,
    existingGroupCodes?: string[],
  ) => void;
  omitSubject: (courseId: string) => void;
  setInferredLeagueCode: (courseId: string, inferredCode: string | null) => void;
  setSectionConfigurations: (courseId: string, sections: SectionConfiguration[]) => void;
  setShouldCreateInitialGroups: (selected: boolean) => void;
  setInitialGroupValues: (courseId: string, values: Partial<InitialGroupValues>) => void;
  setSubjectValidationMessages: (courseId: string, messages: ValidationPresentation[]) => void;
  setGlobalValidationMessages: (messages: ValidationPresentation[]) => void;
  clearValidationMessages: () => void;
  reset: () => void;
}

export type PackageWithLeaguesStore = PackageWithLeaguesState & PackageWithLeaguesActions;

const emptyPackageDetails = (): PackageDetailsValues => ({
  termId: "",
  campusId: "",
  modalityId: "",
  programId: "",
  population: null,
  packageName: "",
});

const emptyAbilities = (): PackageCreationAbilities => ({
  canCreate: false,
  canEditPackages: false,
  canEditLinksAndSections: false,
  canCreateGroupsInBundleView: false,
});

const defaultSettings = (): PackageCreationSettings => ({
  groupCreationMode: "DISABLED",
  canCreateLinkWithoutNewSections: false,
});

export const createInitialPackageWithLeaguesState = (): PackageWithLeaguesState => ({
  currentStep: "package-details",
  packageDetails: emptyPackageDetails(),
  isJointCreationSelected: false,
  abilities: emptyAbilities(),
  settings: defaultSettings(),
  subjects: [],
  shouldCreateInitialGroups: false,
  leagueConfigurationSourceKey: null,
  initialGroupConfigurationSourceKey: null,
  validationMessagesByCourseId: {},
  globalValidationMessages: [],
});

const initialGroupValues = (subject: PopulationSubject): InitialGroupValues => ({
  code: "",
  vacancies: getLockedVacancies(subject.vacancySource) ?? "",
});

const createNewChoice = (subject: PopulationSubject) =>
  ({
    kind: "create-new",
    inferredCode: null,
    sections: subject.components.map(component => ({
      kind: "create-new" as const,
      courseComponentId: component.courseComponentId,
    })),
    group: initialGroupValues(subject),
  }) satisfies SubjectConfiguration["choice"];

const updateSubject = (
  subjects: SubjectConfiguration[],
  courseId: string,
  update: (configuration: SubjectConfiguration) => SubjectConfiguration,
) =>
  subjects.map(configuration =>
    configuration.subject.courseId === courseId ? update(configuration) : configuration,
  );

const withoutSubjectValidation = (
  validationMessagesByCourseId: Record<string, ValidationPresentation[]>,
  courseId: string,
) => {
  const nextMessages = { ...validationMessagesByCourseId };
  delete nextMessages[courseId];
  return nextMessages;
};

export const selectCanUseJointCreation = ({
  abilities,
  settings,
}: Pick<PackageWithLeaguesState, "abilities" | "settings">): boolean =>
  abilities.canCreate &&
  abilities.canEditPackages &&
  abilities.canEditLinksAndSections &&
  (settings.groupCreationMode !== "MANDATORY" || abilities.canCreateGroupsInBundleView);

export const selectNewLeagueConfigurations = ({
  subjects,
}: Pick<PackageWithLeaguesState, "subjects">): SubjectConfiguration[] =>
  subjects.filter(configuration => configuration.choice.kind === "create-new");

export const selectHasIncompleteExistingReferences = ({
  subjects,
}: Pick<PackageWithLeaguesState, "subjects">): boolean =>
  subjects.some(({ choice }) => {
    if (choice.kind === "use-existing") return !choice.existingLeagueId;
    if (choice.kind !== "create-new") return false;

    return choice.sections.some(section => section.kind === "use-existing" && !section.sectionId);
  });

export const selectCanOfferInitialGroups = ({
  abilities,
  settings,
  subjects,
}: Pick<PackageWithLeaguesState, "abilities" | "settings" | "subjects">): boolean =>
  settings.groupCreationMode === "OPTIONAL" &&
  abilities.canCreateGroupsInBundleView &&
  selectNewLeagueConfigurations({ subjects }).length > 0;

export const selectShouldVisitInitialGroups = ({
  abilities,
  settings,
  subjects,
  shouldCreateInitialGroups,
}: Pick<
  PackageWithLeaguesState,
  "abilities" | "settings" | "subjects" | "shouldCreateInitialGroups"
>): boolean => {
  const hasNewLeagues = selectNewLeagueConfigurations({ subjects }).length > 0;

  if (!hasNewLeagues || !abilities.canCreateGroupsInBundleView) return false;
  if (settings.groupCreationMode === "MANDATORY") return true;

  return settings.groupCreationMode === "OPTIONAL" && shouldCreateInitialGroups;
};

const normalizeInitialGroupSelection = (
  mode: GroupCreationMode,
  canCreateGroups: boolean,
  selected: boolean,
  subjects: SubjectConfiguration[],
): boolean => {
  if (!selectNewLeagueConfigurations({ subjects }).length) return false;
  if (!canCreateGroups || mode === "DISABLED") return false;
  if (mode === "MANDATORY") return true;
  return selected;
};

const withSubjects = (
  state: PackageWithLeaguesState,
  subjects: SubjectConfiguration[],
  courseId?: string,
): Partial<PackageWithLeaguesState> => ({
  subjects,
  shouldCreateInitialGroups: normalizeInitialGroupSelection(
    state.settings.groupCreationMode,
    state.abilities.canCreateGroupsInBundleView,
    state.shouldCreateInitialGroups,
    subjects,
  ),
  validationMessagesByCourseId: courseId
    ? withoutSubjectValidation(state.validationMessagesByCourseId, courseId)
    : {},
});

/**
 * Creates one independent store instance for one mounted package-creation route.
 * The route provider owns this instance and calls `reset` when it unmounts.
 */
export const createPackageWithLeaguesStore = () =>
  createStore<PackageWithLeaguesStore>((set, get) => ({
    ...createInitialPackageWithLeaguesState(),

    goForward: () => {
      const state = get();
      const nextStepByStep: Record<PackageCreationWizardStep, PackageCreationWizardStep> = {
        "package-details": "league-configuration",
        "league-configuration": selectShouldVisitInitialGroups(state)
          ? "initial-groups"
          : "league-configuration",
        "initial-groups": "initial-groups",
      };

      set({ currentStep: nextStepByStep[state.currentStep] });
    },

    goBack: () => {
      const state = get();
      const previousStepByStep: Record<PackageCreationWizardStep, PackageCreationWizardStep> = {
        "package-details": "package-details",
        "league-configuration": "package-details",
        "initial-groups": "league-configuration",
      };

      set({ currentStep: previousStepByStep[state.currentStep] });
    },

    setPackageDetails: values =>
      set(state => ({
        packageDetails: { ...state.packageDetails, ...values },
      })),

    prepareLeagueConfiguration: sourceKey =>
      set(state =>
        state.leagueConfigurationSourceKey === sourceKey
          ? {}
          : {
              subjects: [],
              shouldCreateInitialGroups: false,
              leagueConfigurationSourceKey: sourceKey,
              initialGroupConfigurationSourceKey: null,
              validationMessagesByCourseId: {},
              globalValidationMessages: [],
            },
      ),

    prepareInitialGroupConfiguration: sourceKey =>
      set(state => {
        if (state.initialGroupConfigurationSourceKey === sourceKey) return {};

        return {
          subjects: state.subjects.map(configuration => {
            if (configuration.choice.kind !== "create-new") return configuration;

            return {
              ...configuration,
              choice: {
                ...configuration.choice,
                group: initialGroupValues(configuration.subject),
              },
            };
          }),
          initialGroupConfigurationSourceKey: sourceKey,
          validationMessagesByCourseId: {},
          globalValidationMessages: [],
        };
      }),

    setJointCreationSelected: selected =>
      set(state => ({
        isJointCreationSelected: selectCanUseJointCreation(state) && selected,
      })),

    configureFlow: (abilities, settings) =>
      set(state => ({
        abilities: { ...abilities },
        settings: { ...settings },
        isJointCreationSelected:
          selectCanUseJointCreation({ abilities, settings }) && state.isJointCreationSelected,
        shouldCreateInitialGroups: normalizeInitialGroupSelection(
          settings.groupCreationMode,
          abilities.canCreateGroupsInBundleView,
          state.shouldCreateInitialGroups,
          state.subjects,
        ),
      })),

    initializeSubjects: subjects =>
      set(state => {
        const nextSubjects: SubjectConfiguration[] = subjects.map(subject => ({
          subject: { ...subject },
          choice: subject.canCreateNewLeague
            ? createNewChoice(subject)
            : ({ kind: "omit" } as const),
        }));

        return withSubjects(state, nextSubjects);
      }),

    selectCreateNewLeague: courseId =>
      set(state => {
        const subjects = updateSubject(state.subjects, courseId, configuration => {
          if (!configuration.subject.canCreateNewLeague) return configuration;
          if (configuration.choice.kind === "create-new") return configuration;

          return { ...configuration, choice: createNewChoice(configuration.subject) };
        });

        return withSubjects(state, subjects, courseId);
      }),

    selectExistingLeague: (courseId, existingLeagueId = null, existingGroupCodes = []) =>
      set(state => {
        const subjects = updateSubject(state.subjects, courseId, configuration => ({
          ...configuration,
          choice: {
            kind: "use-existing",
            existingLeagueId,
            existingGroupCodes: [...existingGroupCodes],
          },
        }));

        return withSubjects(state, subjects, courseId);
      }),

    omitSubject: courseId =>
      set(state => {
        const subjects = updateSubject(state.subjects, courseId, configuration => ({
          ...configuration,
          choice: { kind: "omit" },
        }));

        return withSubjects(state, subjects, courseId);
      }),

    setInferredLeagueCode: (courseId, inferredCode) =>
      set(state => ({
        subjects: updateSubject(state.subjects, courseId, configuration => {
          if (configuration.choice.kind !== "create-new") return configuration;

          return {
            ...configuration,
            choice: { ...configuration.choice, inferredCode },
          };
        }),
      })),

    setSectionConfigurations: (courseId, sections) =>
      set(state => ({
        subjects: updateSubject(state.subjects, courseId, configuration => {
          if (configuration.choice.kind !== "create-new") return configuration;

          return {
            ...configuration,
            choice: {
              ...configuration.choice,
              sections: sections.map(section => ({ ...section })),
            },
          };
        }),
        validationMessagesByCourseId: withoutSubjectValidation(
          state.validationMessagesByCourseId,
          courseId,
        ),
      })),

    setShouldCreateInitialGroups: selected =>
      set(state => ({
        shouldCreateInitialGroups: normalizeInitialGroupSelection(
          state.settings.groupCreationMode,
          state.abilities.canCreateGroupsInBundleView,
          selected,
          state.subjects,
        ),
      })),

    setInitialGroupValues: (courseId, values) =>
      set(state => ({
        subjects: updateSubject(state.subjects, courseId, configuration => {
          if (configuration.choice.kind !== "create-new") return configuration;
          const lockedVacancies = getLockedVacancies(configuration.subject.vacancySource);

          return {
            ...configuration,
            choice: {
              ...configuration.choice,
              group: {
                ...configuration.choice.group,
                ...values,
                ...(lockedVacancies !== null && { vacancies: lockedVacancies }),
              },
            },
          };
        }),
        validationMessagesByCourseId: withoutSubjectValidation(
          state.validationMessagesByCourseId,
          courseId,
        ),
      })),

    setSubjectValidationMessages: (courseId, messages) =>
      set(state => ({
        validationMessagesByCourseId: {
          ...state.validationMessagesByCourseId,
          [courseId]: messages.map(message => ({ ...message })),
        },
      })),

    setGlobalValidationMessages: messages =>
      set({ globalValidationMessages: messages.map(message => ({ ...message })) }),

    clearValidationMessages: () =>
      set({ validationMessagesByCourseId: {}, globalValidationMessages: [] }),

    reset: () => set(createInitialPackageWithLeaguesState()),
  }));

export type PackageWithLeaguesStoreApi = ReturnType<typeof createPackageWithLeaguesStore>;
